import type { JSX } from "preact";
import { useRef } from "preact/hooks";
import { useSignalEffect } from "@preact/signals";
import { dismissToast } from "../actions";
import {
  confirmRequest,
  drawerOpen,
  mailboxes,
  mobilePane,
  selectedUid,
  toasts,
  view,
} from "../state";
import { prefs, setPref } from "../prefs";
import { t } from "../i18n";
import { Login } from "./Login";
import { Sidebar } from "./Sidebar";
import { MessageList } from "./MessageList";
import { AttachmentViewerModal, EmlPreviewModal, Reader } from "./Reader";
import { Composer } from "./Composer";
import { KeyboardHandler, ShortcutsModal } from "./KeyboardHandler";
import { Settings } from "./Settings";
import { CommandPalette } from "./CommandPalette";
import { MovePickerDialog } from "./MovePicker";
import { Logo } from "./Logo";
import { DESKTOP, Dialog, useMedia } from "./kit";
import { Icon } from "./icons";

export function App(): JSX.Element {
  const current = view.value;

  if (current === "loading") return <Splash />;
  if (current === "login") return <Login />;

  return (
    <>
      <Shell />
      <Composer />
      <EmlPreviewModal />
      <AttachmentViewerModal />
      <MovePickerDialog />
      <ConfirmHost />
      <Toasts />
      <KeyboardHandler />
      <ShortcutsModal />
      <CommandPalette />
      <Settings />
      <TitleUpdater />
    </>
  );
}

const MIN_LIST = 320;
const MAX_LIST = 640;

function Shell(): JSX.Element {
  const desktop = useMedia(DESKTOP);
  const wide = useMedia("(min-width: 1100px)");
  const p = prefs.value;
  const hasMessage = selectedUid.value != null;

  // Layout decision:
  //  - phone: one pane at a time (list ↔ reader)
  //  - desktop + reading pane right: list | reader side by side
  //  - desktop + reading pane off (or a narrow window): list OR reader, full width
  const split = desktop && p.readingPane === "right" && wide;
  const showList = desktop ? split || !hasMessage : mobilePane.value !== "reader";
  const showReader = desktop ? split || hasMessage : mobilePane.value === "reader";

  return (
    <div class="flex h-full overflow-hidden bg-canvas">
      {/* Sidebar: fixed column on desktop, drawer on phones. */}
      {desktop ? (
        <Sidebar collapsed={p.sidebarCollapsed} />
      ) : (
        drawerOpen.value && (
          <div class="fixed inset-0 z-50 flex">
            <div class="absolute inset-0 animate-fade bg-scrim" onClick={() => (drawerOpen.value = false)} />
            <div class="animate-drawer relative h-full w-[min(84vw,300px)] shadow-[var(--shadow-float)]">
              <Sidebar collapsed={false} />
            </div>
          </div>
        )
      )}

      <main class="flex min-w-0 flex-1 overflow-hidden">
        {showList && (
          <div
            class="flex h-full min-w-0 flex-col border-r border-line bg-surface"
            style={split ? `width:${Math.min(MAX_LIST, Math.max(MIN_LIST, p.listWidth))}px;flex:none` : "flex:1"}
          >
            <MessageList />
          </div>
        )}
        {split && <Resizer />}
        {showReader && (
          <div class="flex h-full min-w-0 flex-1 flex-col bg-surface">
            <Reader />
          </div>
        )}
      </main>
    </div>
  );
}

/** Drag handle between the list and the reader. Double-click resets the width. */
function Resizer(): JSX.Element {
  const start = useRef<{ x: number; w: number } | null>(null);
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t("layout.resize")}
      tabIndex={0}
      class="group relative -ml-[3px] w-[6px] shrink-0 cursor-col-resize"
      onPointerDown={(e) => {
        start.current = { x: e.clientX, w: prefs.value.listWidth };
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (!start.current) return;
        const w = Math.min(MAX_LIST, Math.max(MIN_LIST, start.current.w + e.clientX - start.current.x));
        prefs.value = { ...prefs.value, listWidth: w };
      }}
      onPointerUp={() => {
        if (start.current) setPref("listWidth", prefs.value.listWidth);
        start.current = null;
      }}
      onDblClick={() => setPref("listWidth", 400)}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") setPref("listWidth", Math.max(MIN_LIST, prefs.value.listWidth - 24));
        if (e.key === "ArrowRight") setPref("listWidth", Math.min(MAX_LIST, prefs.value.listWidth + 24));
      }}
    >
      <span class="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-transparent transition-colors group-hover:bg-accent group-active:bg-accent" />
    </div>
  );
}

/** Reactively updates document.title based on inbox unread count. */
function TitleUpdater(): null {
  useSignalEffect(() => {
    const count = mailboxes.value.find((m) => m.specialUse === "inbox")?.unread ?? 0;
    document.title = count > 0 ? `(${count}) MailTrack` : "MailTrack";
  });
  return null;
}

function Toasts(): JSX.Element | null {
  const list = toasts.value;
  if (!list.length) return null;
  return (
    <div class="pointer-events-none fixed inset-x-0 bottom-4 z-[70] flex flex-col items-center gap-2 px-4" aria-live="polite">
      {list.map((tt) => (
        <div
          key={tt.id}
          class="animate-rise pointer-events-auto flex max-w-md items-center gap-3 rounded-[10px] bg-fg py-2 pl-4 pr-2 text-[13px] text-canvas shadow-[var(--shadow-float)]"
        >
          {tt.tone === "error" && <Icon name="alert" size={16} class="shrink-0 text-danger" />}
          {tt.tone === "success" && <Icon name="checkCircle" size={16} class="shrink-0 text-success" />}
          <span class="min-w-0 flex-1">{tt.message}</span>
          {tt.action && (
            <button
              class="rounded-md px-2.5 py-1 font-semibold text-toast-action hover:bg-canvas/10"
              onClick={() => {
                tt.action!.run();
                dismissToast(tt.id);
              }}
            >
              {tt.action.label}
            </button>
          )}
          <button
            class="rounded-md p-1 opacity-60 hover:opacity-100"
            onClick={() => dismissToast(tt.id)}
            aria-label={t("common.close")}
          >
            <Icon name="x" size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}

function ConfirmHost(): JSX.Element | null {
  const req = confirmRequest.value;
  if (!req) return null;
  return (
    <Dialog onClose={() => req.resolve(false)} label={req.title} class="max-w-sm">
      <div class="px-5 pb-4 pt-5">
        <h2 class="text-[15px] font-semibold text-fg">{req.title}</h2>
        {req.body && <p class="mt-1.5 text-[13px] leading-relaxed text-fg-3">{req.body}</p>}
      </div>
      <div class="flex justify-end gap-2 border-t border-line bg-canvas/50 px-5 py-3">
        <button class="btn btn-ghost" onClick={() => req.resolve(false)}>
          {t("common.cancel")}
        </button>
        <button class={`btn ${req.danger ? "btn-danger" : "btn-primary"}`} autofocus onClick={() => req.resolve(true)}>
          {req.confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}

function Splash(): JSX.Element {
  return (
    <div class="flex h-full flex-col items-center justify-center gap-4 bg-canvas">
      <Logo size={40} />
      <span class="h-0.5 w-24 overflow-hidden rounded-full bg-line">
        <span class="block h-full w-1/3 animate-[splash_1s_ease-in-out_infinite] rounded-full bg-accent" />
      </span>
    </div>
  );
}
